import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import useWebSocket from '../../api/websocket';

export default function ServerList() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  const { subscribe } = useWebSocket();

  useEffect(() => {
    fetchServers();
    const unsub = subscribe('stream_status', (data) => {
      setServers((prev) =>
        prev.map((s) => (s.id === data.payload.id ? { ...s, ...data.payload } : s))
      );
    });
    return unsub;
  }, [subscribe]);

  const fetchServers = async () => {
    try {
      const { data } = await api.get('/api/streaming/servers');
      setServers(data);
    } catch (err) {
      console.error('[ServerList] Failed to fetch servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (server) => {
    try {
      const { data } = await api.put(`/api/streaming/servers/${server.id}`, {
        is_active: !server.is_active,
      });
      setServers(servers.map((s) => (s.id === server.id ? { ...s, ...data } : s)));
    } catch (err) {
      console.error('[ServerList] Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/streaming/servers/${confirmItem.id}`);
      setServers(servers.filter((s) => s.id !== confirmItem.id));
    } catch (err) {
      console.error('[ServerList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading servers...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Streaming Servers</h1>
        <Link to="/streaming/create" className="btn btn-primary btn-sm">
          + New Server
        </Link>
      </div>

      {servers.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No servers configured. Create your first streaming server.
        </div>
      ) : (
        <div className="space-y-3">
          {servers.map((server) => (
            <div key={server.id} className="rounded-xl bg-dark-850 border border-dark-700 p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{server.is_default ? '📡' : '📶'}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-dark-100">{server.name}</h3>
                      {server.is_default && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary-900/30 text-primary-400">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-dark-400">
                      {server.host}:{server.port}
                      {server.mount && <span className="ml-2">📍 {server.mount}</span>}
                    </p>
                    {server.description && (
                      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-dark-500">
                        {server.description}
                      </p>
                    )}
                  </div>
                </div>

                <StatusBadge status={server.status || 'disconnected'} size="md" />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className="text-xs text-dark-500">Protocol</p>
                  <p className="text-sm font-medium text-dark-100 uppercase">{server.protocol || 'Icecast'}</p>
                </div>
                <div>
                  <p className="text-xs text-dark-500">Bitrate</p>
                  <p className="text-sm font-medium text-dark-100">
                    {server.bitrate ? `${server.bitrate} kbps` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-dark-500">Listeners</p>
                  <p className="text-sm font-medium text-primary-400">
                    {server.listener_count || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-dark-500">Max</p>
                  <p className="text-sm font-medium text-dark-100">
                    {server.max_listeners || '∞'}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-dark-700 pt-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggleActive(server)}
                    className="text-xs text-dark-400 hover:text-dark-200"
                    title={server.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {server.is_active ? '● Active' : '○ Inactive'}
                  </button>
                  {server.status === 'connected' && (
                    <span className="text-xs text-success-400">● Connected</span>
                  )}
                  {server.status === 'disconnected' && (
                    <span className="text-xs text-error-400">○ Disconnected</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/streaming/${server.id}/edit`}
                    className="text-sm text-dark-400 hover:text-dark-200"
                    title="Edit"
                  >
                    ✎
                  </Link>
                  <button
                    onClick={() => {
                      setConfirmItem(server);
                      setConfirmOpen(true);
                    }}
                    className="text-sm text-error-400 hover:text-error-300"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Server"
        message={`Are you sure you want to delete "${confirmItem?.name}"? This will stop streaming to this server.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
